import { Component, OnInit } from '@angular/core';
import { ChartOfAccounts } from "./chart-of-accounts.model";
import { ChartOfAccountsService } from "./chart-of-accounts.service";
import { ConfirmationService, MessageService, TreeNode } from "primeng/api";
import { SessionStorageService } from "../services/session-storage.service";
import { Router } from "@angular/router";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { TreeTableModule } from 'primeng/treetable';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../shared/page-layout/page-metric-strip.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../shared/page-layout/page-layout.models';

@Component({
    selector: 'app-chart-of-accounts',
    templateUrl: './chart-of-accounts.component.html',
    styleUrls: ['./chart-of-accounts.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ToastModule,
        ProgressBarModule,
        BreadcrumbModule,
        TreeTableModule,
        ButtonModule,
        RippleModule,
        TagModule,
        TooltipModule,
        InputTextModule,
        SelectModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    providers: [
        ChartOfAccountsService,
        MessageService,
        SessionStorageService,
        ConfirmationService
    ]
})
export class ChartOfAccountsComponent implements OnInit {
    private allAccounts: ChartOfAccounts[] = [];
    chartOfAccountsTree: TreeNode<ChartOfAccounts>[] = [];
    flatChartOfAccounts: ChartOfAccounts[] = [];
    selectedChartOfAccounts: ChartOfAccounts[] = [];
    isLoading: boolean = false;
    matchingAccountCount = 0;
    searchQuery = '';
    selectedAccountTypeId: number | null = null;
    selectedStatus: string | null = null;
    selectedReconciliation: boolean | null = null;
    selectedCurrency: string | null = null;
    selectedProtected: boolean | null = null;

    readonly statusOptions = [
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'Needs Review', value: 'Needs Review' },
        { label: 'Deprecated', value: 'Deprecated' }
    ];
    readonly yesNoOptions = [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ];

    get accountTypeOptions(): { label: string; value: number }[] {
        const types = new Map<number, string>();
        for (const account of this.flatChartOfAccounts) {
            const type = account.accountType;
            if (type?.accountTypeId != null) types.set(type.accountTypeId, type.accountTypeName);
        }
        return [...types].map(([value, label]) => ({ label, value }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    get currencyOptions(): { label: string; value: string }[] {
        return [...new Set(this.flatChartOfAccounts.map(account => account.currencyCode || 'SAR'))]
            .sort().map(value => ({ label: value, value }));
    }

    get hasActiveFilters(): boolean {
        return !!this.searchQuery.trim() || this.selectedAccountTypeId != null
            || this.selectedStatus != null || this.selectedReconciliation != null
            || this.selectedCurrency != null || this.selectedProtected != null;
    }

    get resultText(): string {
        return this.hasActiveFilters
            ? `${this.matchingAccountCount} matching accounts · ${this.flatChartOfAccounts.length} total`
            : `${this.flatChartOfAccounts.length} accounts`;
    }

    constructor(
        private chartOfAccountsService: ChartOfAccountsService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        this.isLoading = true;
        this.chartOfAccountsService.getAllChartOfAccountsByCompany(this.sessionStorageService.getCompany()?.id ?? 0, true).subscribe({
            next: (chartOfAccounts) => {
                this.allAccounts = chartOfAccounts;
                this.flatChartOfAccounts = this.flattenAccounts(chartOfAccounts);
                this.applyFilters();
                this.isLoading = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading chart of accounts'
                });
                this.isLoading = false;
            }
        });
    }

    buildTreeNode(chartOfAccounts: ChartOfAccounts): TreeNode<ChartOfAccounts> {
        return {
            label: chartOfAccounts.accountName,
            data: chartOfAccounts,
            key: chartOfAccounts.accountId.toString(),
            expandedIcon: 'pi pi-folder-open',
            collapsedIcon: 'pi pi-folder',
            leaf: (chartOfAccounts.childrenAccounts?.length ?? 0) === 0,
            expanded: false,
            children: chartOfAccounts.childrenAccounts?.map(child => this.buildTreeNode(child))
        };
    }

    applyFilters(): void {
        if (!this.hasActiveFilters) {
            this.chartOfAccountsTree = this.allAccounts.map(account => this.buildTreeNode(account));
            this.matchingAccountCount = this.flatChartOfAccounts.length;
            return;
        }

        this.matchingAccountCount = 0;
        const query = this.searchQuery.trim().toLocaleLowerCase();
        this.chartOfAccountsTree = this.filterTree(this.allAccounts, query, false);
    }

    clearFilters(): void {
        this.searchQuery = '';
        this.selectedAccountTypeId = null;
        this.selectedStatus = null;
        this.selectedReconciliation = null;
        this.selectedCurrency = null;
        this.selectedProtected = null;
        this.applyFilters();
    }

    private filterTree(accounts: ChartOfAccounts[], query: string, ancestorMatchesSearch: boolean): TreeNode<ChartOfAccounts>[] {
        const nodes: TreeNode<ChartOfAccounts>[] = [];
        for (const account of accounts) {
            const ownSearchMatch = !query || [account.accountName, account.accountCode,
                account.accountType?.accountTypeName, account.internalType]
                .some(value => value?.toLocaleLowerCase().includes(query));
            const matchesSearch = ancestorMatchesSearch || ownSearchMatch;
            const matchesFields = (this.selectedAccountTypeId == null
                    || account.accountType?.accountTypeId === this.selectedAccountTypeId)
                && (this.selectedStatus == null || this.accountStatus(account) === this.selectedStatus)
                && (this.selectedReconciliation == null
                    || !!account.allowReconciliation === this.selectedReconciliation)
                && (this.selectedCurrency == null
                    || (account.currencyCode || 'SAR') === this.selectedCurrency)
                && (this.selectedProtected == null
                    || this.isProtectedPostingAccount(account) === this.selectedProtected);
            const children = this.filterTree(account.childrenAccounts || [], query,
                ancestorMatchesSearch || (!!query && ownSearchMatch));
            if (matchesSearch && matchesFields) this.matchingAccountCount++;
            if ((matchesSearch && matchesFields) || children.length) {
                nodes.push({
                    ...this.buildTreeNode(account),
                    children,
                    leaf: children.length === 0,
                    expanded: children.length > 0
                });
            }
        }
        return nodes;
    }

    editChartOfAccounts(chartOfAccounts: ChartOfAccounts) {
        this.router.navigate(['/chart-of-accounts/edit', chartOfAccounts.accountId]);
    }

    viewChartOfAccounts(chartOfAccounts: ChartOfAccounts) {
        this.router.navigate(['/chart-of-accounts/view', chartOfAccounts.accountId]);
    }

    deleteChartOfAccounts(chartOfAccounts: ChartOfAccounts) {
        this.chartOfAccountsService.deleteChartOfAccounts(chartOfAccounts.accountId, chartOfAccounts.owningCompanyId!).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Chart of account deleted successfully.'
                });
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error deleting chart of account'
                });
            },
            complete: () => {
                this.loadData();
            }
        })
    }

    addChildChartOfAccounts(chartOfAccounts: ChartOfAccounts) {
        this.router.navigate(['/chart-of-accounts/add', chartOfAccounts.accountId]);
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Accounts', value: this.flatChartOfAccounts.length, hint: `${this.allAccounts.length} roots`, tone: 'info' },
            { label: 'Active', value: this.flatChartOfAccounts.filter(account => account.isActive).length, tone: 'success' },
            { label: 'Reconciliation', value: this.flatChartOfAccounts.filter(account => account.allowReconciliation).length, tone: 'default' },
            { label: 'Protected', value: this.flatChartOfAccounts.filter(account => this.isProtectedPostingAccount(account)).length, hint: 'Posting dependencies', tone: 'warn' }
        ];
    }

    isProtectedPostingAccount(account: ChartOfAccounts): boolean {
        return this.protectedPostingAccountNames.has(account.accountName);
    }

    accountStatus(account: ChartOfAccounts): string {
        if (account.deprecated) return 'Deprecated';
        if (account.isActive === false) return 'Inactive';
        if (account.isActive === null || account.isActive === undefined) return 'Needs Review';
        return 'Active';
    }

    accountStatusSeverity(account: ChartOfAccounts): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (account.deprecated) return 'danger';
        if (account.isActive === false) return 'secondary';
        if (account.isActive === null || account.isActive === undefined) return 'warn';
        return 'success';
    }

    accountDepth(rowNode: TreeNode<ChartOfAccounts> & { level?: number }): number {
        return rowNode?.level ?? 0;
    }

    childAccountCount(account: ChartOfAccounts): number {
        return account.childrenAccounts?.length ?? 0;
    }

    balanceValue(account: ChartOfAccounts): number {
        return Number(account.currentBalance ?? 0);
    }

    balanceMagnitude(account: ChartOfAccounts): number {
        return Math.abs(this.balanceValue(account));
    }

    balanceSide(account: ChartOfAccounts): string {
        const balance = this.balanceValue(account);
        if (balance > 0) return 'Dr';
        if (balance < 0) return 'Cr';
        return 'Zero';
    }

    isCreditBalance(account: ChartOfAccounts): boolean {
        return this.balanceValue(account) < 0;
    }

    isZeroBalance(account: ChartOfAccounts): boolean {
        return this.balanceValue(account) === 0;
    }

    private flattenAccounts(accounts: ChartOfAccounts[]): ChartOfAccounts[] {
        return accounts.flatMap(account => [
            account,
            ...this.flattenAccounts(account.childrenAccounts || [])
        ]);
    }

    private readonly protectedPostingAccountNames = new Set([
        'Sales',
        'Trade Accounts Receivable',
        'Sales Tax Payable',
        'Cash and Cash Equivalents',
        'Trade Accounts Payable',
        'Driver Advances',
        'Employee Advances',
        'Expenses',
        'Input VAT Account',
        'Driver Trip Expenses',
        'Workforce Cash Variance',
        'Workforce Cash Return'
    ]);

}
